import { Box, Checkbox, Chip, Paper, Stack, Typography } from '@mui/material';
import { parseEntryData } from '../../api/queries/taxonomy';
import type { TaxonomyEntryDto } from '../../api/queries/taxonomy';

interface Props {
  entry: TaxonomyEntryDto;
  selected?: boolean;
  onToggleSelect?: (id: string) => void;
  onClick?: (id: string) => void;
  multiSelectMode?: boolean;
}

export function TaxonomyEntryCard({
  entry,
  selected = false,
  onToggleSelect,
  onClick,
  multiSelectMode = false,
}: Props) {
  const data = parseEntryData(entry.data);
  const description =
    typeof data.description === 'string' ? data.description : '';
  const traits = Array.isArray(data.traits)
    ? (data.traits as string[]).slice(0, 4)
    : Array.isArray(data.powers)
      ? (data.powers as string[]).slice(0, 4)
      : Array.isArray(data.abilities)
        ? (data.abilities as string[]).slice(0, 4)
        : [];

  return (
    <Paper
      variant="outlined"
      sx={{
        overflow: 'hidden',
        cursor: 'pointer',
        position: 'relative',
        transition: 'box-shadow 0.15s, border-color 0.15s',
        borderColor: selected ? 'primary.main' : 'divider',
        borderWidth: selected ? 2 : 1,
        '&:hover': { boxShadow: 3 },
      }}
      onClick={() => {
        if (multiSelectMode && onToggleSelect) {
          onToggleSelect(entry.id);
        } else if (onClick) {
          onClick(entry.id);
        }
      }}
    >
      {multiSelectMode && (
        <Checkbox
          checked={selected}
          size="small"
          sx={{ position: 'absolute', top: 4, left: 4, zIndex: 1 }}
          onClick={(e) => {
            e.stopPropagation();
            onToggleSelect?.(entry.id);
          }}
        />
      )}

      <Box sx={{ p: 2 }}>
        <Typography variant="subtitle2" noWrap fontWeight={600}>
          {entry.name}
        </Typography>

        {description && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mt: 0.5,
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              fontSize: 13,
              lineHeight: 1.4,
            }}
          >
            {description}
          </Typography>
        )}

        {traits.length > 0 && (
          <Stack
            direction="row"
            spacing={0.5}
            sx={{ mt: 1, flexWrap: 'wrap', gap: 0.5 }}
          >
            {traits.map((t) => (
              <Chip key={t} label={t} size="small" variant="outlined" />
            ))}
          </Stack>
        )}
      </Box>
    </Paper>
  );
}
